import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Filter, X, SlidersHorizontal, ShieldCheck, FileCheck2, MapPin, Compass, Ruler, Award, Sparkles, CornerUpRight, Eye } from "lucide-react";
import { api, formatPrice, formatApiError, asItems } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";

const FACING = ["North", "South", "East", "West", "North-East", "North-West", "South-East", "South-West"];
const AREA_UNITS = ["sq_yard", "sq_meter", "sq_ft", "acre", "hectare", "bigha", "katha"];
const SORT = [
  { v: "newest", l: "Newest" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
  { v: "verified_first", l: "Verified Owner First" },
  { v: "most_viewed", l: "Most Viewed" },
];

function formatArea(area, unit) {
  if (area == null) return "—";
  const u = (unit || "sq_yard").replace("_", " ");
  return `${Number(area).toLocaleString("en-IN")} ${u}`;
}

function pricePerUnit(p) {
  const ld = p.land_details || p.land || {};
  const a = ld.area || p.area;
  if (!a || a <= 0) return null;
  return p.price / a;
}

export default function Plots() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(() => readParams(searchParams));

  useEffect(() => { setDraft(readParams(searchParams)); }, [searchParams]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = readParams(searchParams);
      const qs = new URLSearchParams();
      qs.set("category", params.category || "PLOT");
      Object.entries(params).forEach(([k, v]) => {
        if (k === "category") return;
        if (v == null || v === "" || v === "any") return;
        qs.set(k, v);
      });
      qs.set("limit", "24");
      const { data } = await api.get(`/v1/search/properties?${qs.toString()}`);
      setItems(asItems(data));
      setTotal(data.total || 0);
    } catch (e) {
      setItems([]); setTotal(0);
      setError(formatApiError(e) || "Plots could not be loaded.");
    } finally { setLoading(false); }
  }, [searchParams]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const applyDraft = () => {
    const out = {};
    Object.entries(draft).forEach(([k, v]) => {
      if (v == null || v === "" || v === "any") return;
      out[k] = String(v);
    });
    setSearchParams(out);
  };

  const reset = () => { setDraft({}); setSearchParams({}); };
  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const activeChips = useMemo(() => {
    const chips = [];
    if (draft.city) chips.push({ k: "city", l: `City: ${draft.city}` });
    if (draft.locality) chips.push({ k: "locality", l: draft.locality });
    if (draft.max_price) chips.push({ k: "max_price", l: `≤ ${formatPrice(draft.max_price)}` });
    if (draft.min_price) chips.push({ k: "min_price", l: `≥ ${formatPrice(draft.min_price)}` });
    if (draft.min_area) chips.push({ k: "min_area", l: `Area ≥ ${draft.min_area}` });
    if (draft.max_area) chips.push({ k: "max_area", l: `Area ≤ ${draft.max_area}` });
    if (draft.facing) chips.push({ k: "facing", l: `Facing: ${draft.facing}` });
    if (draft.road_width_min) chips.push({ k: "road_width_min", l: `Road ≥ ${draft.road_width_min}ft` });
    if (draft.corner_plot === "true") chips.push({ k: "corner_plot", l: "Corner Plot" });
    if (draft.registry_available === "true") chips.push({ k: "registry_available", l: "Registry Ready" });
    if (draft.mutation_available === "true") chips.push({ k: "mutation_available", l: "Mutation Done" });
    if (draft.verified_owner === "true") chips.push({ k: "verified_owner", l: "Verified Owner" });
    return chips;
  }, [draft]);

  return (
    <div className="premium-page-surface bg-transparent min-h-screen" data-testid="plots-page">
      {/* Hero strip */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] opacity-80 mb-2">India&apos;s Land-First Marketplace</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            {/* Brand gradient accent on the lead word (pops on the dark hero) */}
            <span className="brand-gradient-text">Land</span> &amp; Plots
          </h1>
          <p className="opacity-90 mt-3 max-w-2xl">
            Compare published land and plot listings with the available owner, registry, mutation, and document-verification status shown clearly.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
              <ShieldCheck className="w-3.5 h-3.5"/> Owner Status Shown
            </span>
            <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
              <FileCheck2 className="w-3.5 h-3.5"/> Registry Status Shown
            </span>
            <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5"/> Plots on Demand
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Inventory</p>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-primary" data-testid="plots-total">
              <span className="brand-gradient-text">{Number(total || 0).toLocaleString("en-IN")}</span> {total === 1 ? "plot" : "plots"} available
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={draft.category || "PLOT"}
              onValueChange={(v) => {
                updateDraft({ category: v });
                const sp = new URLSearchParams(searchParams); sp.set("category", v); setSearchParams(sp);
              }}
            >
              <SelectTrigger className="w-40" data-testid="plots-cat-trigger"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="PLOT">Plots</SelectItem>
                <SelectItem value="LAND">Land</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={draft.sort || "newest"}
              onValueChange={(v) => { updateDraft({ sort: v }); const sp = new URLSearchParams(searchParams); sp.set("sort", v); setSearchParams(sp); }}
            >
              <SelectTrigger className="w-52" data-testid="plots-sort-trigger"><SelectValue/></SelectTrigger>
              <SelectContent>
                {SORT.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="gap-2 font-bold" data-testid="plots-filter-trigger">
                  <SlidersHorizontal className="w-4 h-4"/>Filters
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto" data-testid="plots-filter-sheet">
                <SheetHeader><SheetTitle className="font-display text-2xl">Plot Filters</SheetTitle></SheetHeader>
                <div className="mt-6 space-y-6">
                  <Group label="Location">
                    <Input placeholder="City (e.g. Noida, Gurugram, Pune)" value={draft.city || ""} onChange={(e) => updateDraft({ city: e.target.value })} data-testid="plots-filter-city"/>
                    <Input placeholder="Locality / Sector" value={draft.locality || ""} onChange={(e) => updateDraft({ locality: e.target.value })} className="mt-2" data-testid="plots-filter-locality"/>
                  </Group>
                  <Group label="Price Range">
                    <PriceRangeSlider
                      min={Number(draft.min_price) || 0}
                      max={Number(draft.max_price) || 100000000}
                      onChange={(lo, hi) => updateDraft({ min_price: lo || "", max_price: hi >= 100000000 ? "" : hi })}
                    />
                  </Group>
                  <Group label="Area">
                    <div className="flex gap-2">
                      <Input type="number" min="0" placeholder="Min" value={draft.min_area || ""} onChange={(e) => updateDraft({ min_area: e.target.value })} data-testid="plots-filter-area-min"/>
                      <Input type="number" min="0" placeholder="Max" value={draft.max_area || ""} onChange={(e) => updateDraft({ max_area: e.target.value })} data-testid="plots-filter-area-max"/>
                    </div>
                    <Select value={draft.area_unit || "sq_yard"} onValueChange={(v) => updateDraft({ area_unit: v })}>
                      <SelectTrigger className="mt-2" data-testid="plots-filter-area-unit"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {AREA_UNITS.map(u => <SelectItem key={u} value={u}>{u.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Group>
                  <Group label="Facing">
                    <Select value={draft.facing || "any"} onValueChange={(v) => updateDraft({ facing: v === "any" ? "" : v })}>
                      <SelectTrigger data-testid="plots-filter-facing"><SelectValue placeholder="Any"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {FACING.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Group>
                  <Group label="Minimum Road Width (ft)">
                    <Select value={draft.road_width_min || "any"} onValueChange={(v) => updateDraft({ road_width_min: v === "any" ? "" : v })}>
                      <SelectTrigger data-testid="plots-filter-road"><SelectValue placeholder="Any"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {[15, 20, 24, 30, 40, 50, 60, 80, 100].map(w => <SelectItem key={w} value={String(w)}>{w}+ ft</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Group>
                  <Group label="Document Status">
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="plots-filter-registry">
                        <Checkbox checked={draft.registry_available === "true"} onCheckedChange={(v) => updateDraft({ registry_available: v ? "true" : "" })}/>
                        Registry available
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="plots-filter-mutation">
                        <Checkbox checked={draft.mutation_available === "true"} onCheckedChange={(v) => updateDraft({ mutation_available: v ? "true" : "" })}/>
                        Mutation done
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="plots-filter-corner">
                        <Checkbox checked={draft.corner_plot === "true"} onCheckedChange={(v) => updateDraft({ corner_plot: v ? "true" : "" })}/>
                        Corner plot only
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="plots-filter-verified-owner">
                        <Checkbox checked={draft.verified_owner === "true"} onCheckedChange={(v) => updateDraft({ verified_owner: v ? "true" : "" })}/>
                        Verified owner only
                      </label>
                    </div>
                  </Group>
                  <div className="flex gap-2 pt-4 sticky bottom-0 bg-background pb-4">
                    <Button variant="outline" onClick={reset} className="flex-1" data-testid="plots-filter-reset">Reset</Button>
                    <Button onClick={applyDraft} className="flex-1 font-bold" data-testid="plots-filter-apply">Apply</Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="plots-active-chips">
            {activeChips.map(c => (
              <button type="button" key={c.k} onClick={() => { const sp = new URLSearchParams(searchParams); sp.delete(c.k); setSearchParams(sp); }}
                className="text-xs font-bold uppercase tracking-wider bg-secondary text-primary px-3 py-1.5 rounded-full flex items-center gap-1.5 hover:bg-border">
                {c.l} <X className="w-3 h-3"/>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plots-loading">
            {[...Array(6)].map((_,i) => <div key={i} className="h-96 shimmer rounded-lg"/>)}
          </div>
        ) : error ? (
          <div className="py-20 text-center border border-destructive/30 bg-destructive/5 rounded-2xl" role="alert" data-testid="plots-error">
            <h3 className="font-display text-2xl font-bold text-primary">Plots could not be loaded</h3>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Button type="button" className="mt-5" onClick={fetchList}>Try again</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-border rounded-lg" data-testid="plots-empty">
            <Filter className="w-10 h-10 mx-auto mb-4 text-muted-foreground"/>
            <h3 className="font-display text-2xl font-bold text-primary">No plots match your filters</h3>
            <p className="text-muted-foreground mt-2">Try widening your criteria — or request a custom plot.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Button variant="outline" onClick={reset} data-testid="plots-empty-reset">Reset filters</Button>
              <Button asChild className="font-bold" data-testid="plots-empty-demand"><Link to="/plot-on-demand">Open Plots on Demand</Link></Button>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plots-grid">
            {items.map(p => <PlotCard key={p.property_id} p={p}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

const FALLBACK_IMAGE = "/brand/real-place-mark.svg";

function PlotCard({ p }) {
  const ld = p.land_details || p.land || {};
  const boosted = Boolean(p.boosted) && p.boosted_until && new Date(p.boosted_until) > new Date();
  const ppu = pricePerUnit(p);
  const id = p.property_id || p.id;
  const img = p.images?.[0] || FALLBACK_IMAGE;
  const imageUnavailable = !p.images?.[0];
  const isRecentlyViewed = (() => {
    try {
      const list = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
      return list.includes(id);
    } catch {
      return false;
    }
  })();

  return (
    <Link 
      to={`/properties/${id}`} 
      className="group block bg-white border border-slate-200/80 rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(15,23,42,0.015)] hover:shadow-[0_20px_50px_rgba(0,120,219,0.08)] hover:-translate-y-1.5 transition-all duration-300 text-left" 
      data-testid={`plot-card-${id}`}
    >
      {/* Image Area */}
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-950">
        <img 
          src={img} 
          alt={imageUnavailable ? "Property image unavailable" : p.title}
          className={`w-full h-full group-hover:scale-[1.03] transition-transform duration-700 ${imageUnavailable ? "object-contain bg-slate-100 p-12" : "object-cover"}`}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/15" />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start z-10">
          {boosted && (
            <Badge className="bg-amber-500 hover:bg-amber-500 text-white font-bold uppercase tracking-wider text-[10px] gap-1 px-2.5 py-1 shadow-lg">
              <Award className="w-3 h-3"/> Featured
            </Badge>
          )}
          {p.verified_owner && (
            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-bold uppercase tracking-wider text-[10px] gap-1 px-2.5 py-1 shadow-md">
              <ShieldCheck className="w-3 h-3"/> Verified Owner
            </Badge>
          )}
          {p.verified_documents && (
            <Badge className="bg-blue-600 hover:bg-blue-600 text-white font-bold uppercase tracking-wider text-[10px] gap-1 px-2.5 py-1 shadow-md">
              <FileCheck2 className="w-3 h-3"/> Docs Verified
            </Badge>
          )}
          {isRecentlyViewed && (
            <Badge className="bg-slate-900/80 backdrop-blur hover:bg-slate-900/80 text-white font-bold uppercase tracking-wider text-[10px] gap-1 px-2.5 py-1 shadow-md">
              <Eye className="w-3 h-3"/> Recently Seen
            </Badge>
          )}
        </div>

        {/* Bottom indicators on image */}
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5 z-10">
          {ld.registry_available && (
            <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-500 text-white px-2.5 py-1 rounded-full shadow-sm border border-emerald-400/20">
              Registry Ready
            </span>
          )}
          {ld.mutation_available && (
            <span className="text-[9px] font-black uppercase tracking-wider bg-blue-500 text-white px-2.5 py-1 rounded-full shadow-sm border border-blue-400/20">
              Mutation Done
            </span>
          )}
        </div>
      </div>

      {/* Details Area */}
      <div className="p-5 flex flex-col justify-between">
        <div>
          {/* Price & Unit Price Header */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 mb-2.5">
            <div>
              <span className="text-2xl font-black text-slate-900 font-display tracking-tight leading-none">
                {formatPrice(p.price)}
              </span>
              {ppu && (
                <span className="text-xs font-semibold text-slate-500 ml-2 select-none">
                  (@ {formatPrice(ppu)}/{ld.area_unit === "sq_ft" ? "sq ft" : "sq yard"})
                </span>
              )}
            </div>
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
              {ld.zoning || "PLOT / LAND"}
            </span>
          </div>

          {/* Title */}
          <h3 className="font-display text-base font-bold text-slate-800 group-hover:text-emerald-600 transition leading-snug line-clamp-1 mb-1.5">
            {p.title}
          </h3>

          {/* Location */}
          <p className="text-xs font-medium text-slate-500 flex items-center gap-1 mb-4 select-none">
            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0"/> 
            <span className="line-clamp-1">
              {[p.location?.locality || p.location?.sector, p.location?.city].filter(Boolean).join(", ")}
            </span>
          </p>

          {/* Land attributes grid */}
          <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 text-xs font-semibold text-slate-600">
            <div className="flex items-center gap-2">
              <Ruler className="w-4 h-4 text-slate-400 shrink-0"/>
              <span>{formatArea(ld.area, ld.area_unit)}</span>
            </div>

            {ld.facing && (
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-slate-400 shrink-0"/>
                <span>{ld.facing} Facing</span>
              </div>
            )}

            {ld.road_width_ft && (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-1-4h8l-1 4M10 4l-1 4h6l-1-4M12 8v4" />
                </svg>
                <span>{ld.road_width_ft} ft road</span>
              </div>
            )}

            {ld.corner_plot && (
              <div className="flex items-center gap-2">
                <CornerUpRight className="w-4 h-4 text-emerald-600 shrink-0"/>
                <span className="text-emerald-700 font-bold uppercase tracking-wider text-[10px]">Corner Plot</span>
              </div>
            )}
          </div>
        </div>

        {/* Muted colony / gated colony tags */}
        <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3.5">
          {ld.gated_colony && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700 px-2.5 py-0.5 rounded border border-purple-100/50">
              Gated Colony
            </span>
          )}
          {ld.boundary_wall && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-50 text-slate-600 px-2.5 py-0.5 rounded border border-slate-100/50">
              Boundary Wall
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function readParams(sp) {
  const out = {};
  for (const [k, v] of sp.entries()) out[k] = v;
  return out;
}

function Group({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function PriceRangeSlider({ min, max, onChange }) {
  const MAX_L = 2000;
  const lo = Math.max(0, Math.min(MAX_L, Math.round((min || 0) / 1e5)));
  const hi = Math.max(lo, Math.min(MAX_L, Math.round((max || MAX_L * 1e5) / 1e5)));
  const [v, setV] = React.useState([lo, hi]);
  React.useEffect(() => { setV([lo, hi]); /* eslint-disable-next-line */ }, [min, max]);

  const fmt = (lk) => {
    if (lk >= 100) return `₹${(lk / 100).toFixed(lk % 100 === 0 ? 0 : 2)} Cr`;
    return `₹${lk}L`;
  };

  return (
    <div data-testid="plots-price-range">
      <Slider value={v} min={0} max={MAX_L} step={5} onValueChange={setV}
        onValueCommit={(val) => onChange(val[0] * 1e5, val[1] * 1e5)} className="my-3"/>
      <div className="flex items-center justify-between text-xs text-muted-foreground font-semibold">
        <span>Min: {fmt(v[0])}</span>
        <span>Max: {v[1] >= MAX_L ? "₹20 Cr+" : fmt(v[1])}</span>
      </div>
    </div>
  );
}
